// src/pages/admin/Settings/ViewRoles.js
import React, { useState, useEffect } from 'react';
import { message, Modal, Form, Input, Button, Table, Checkbox, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const ViewRoles = ({ onLogout }) => { // Added onLogout prop
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isPermissionsModalVisible, setIsPermissionsModalVisible] = useState(false);
  const [isViewPermissionsModalVisible, setIsViewPermissionsModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [form] = Form.useForm();

  const initialRoles = [
    { id: 1, name: 'ADMIN', description: 'Users with full administrative access to manage the JectHub system.', status: 'Active', permissions: ['users:*', 'settings:*', 'system:*'] },
    { id: 2, name: 'ACCOUNTANT', description: 'Manages financial records, transactions, and budgeting for JectHub.', status: 'Active', permissions: ['transactions:*', 'reports:financial'] },
    { id: 3, name: 'SUPPORT', description: 'Provides customer support and resolves user queries for JectHub users.', status: 'Active', permissions: ['users:read', 'student:messages'] },
    { id: 4, name: 'HR', description: 'Handles recruitment, onboarding, and staff management for JectHub employees.', status: 'Active', permissions: ['system-users:*'] },
    { id: 5, name: 'OPERATIONS', description: 'Oversees daily operations, system backups, and vendor coordination.', status: 'Inactive', permissions: ['system:*', 'vendors:*'] },
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      setRoles(initialRoles);
      setFilteredRoles(initialRoles);
      setLoading(false);
    }, 500);

    // Placeholder for real API call
    /*
    fetch('/api/roles')
      .then((response) => response.json())
      .then((data) => {
        setRoles(data);
        setFilteredRoles(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching roles:', error);
        setLoading(false);
      });
    */
  }, []);

  const handleSearch = (value) => {
    const filtered = roles.filter(
      (role) =>
        role.name.toLowerCase().includes(value.toLowerCase()) ||
        role.description.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredRoles(filtered);
    setCurrentPage(1);
  };

  const handleAddRole = (values) => {
    const newRole = {
      id: roles.length + 1,
      name: values.name,
      description: values.description,
      status: 'Active',
      permissions: [],
    };
    const updatedRoles = [...roles, newRole];
    setRoles(updatedRoles);
    setFilteredRoles(updatedRoles);
    setIsAddModalVisible(false);
    form.resetFields();
    message.success(`Role "${newRole.name}" added successfully`);
  };

  const handleDeactivateRole = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    const newStatus = role.status === 'Active' ? 'Inactive' : 'Active';
    Modal.confirm({
      title: `${newStatus === 'Inactive' ? 'Deactivate' : 'Activate'} Role`,
      content: `Are you sure you want to ${newStatus.toLowerCase()} the "${role.name}" role?`,
      onOk: () => {
        const updatedRoles = roles.map((r) => (r.id === roleId ? { ...r, status: newStatus } : r));
        setRoles(updatedRoles);
        setFilteredRoles(updatedRoles);
        message.success(`Role "${role.name}" ${newStatus.toLowerCase()}d successfully`);
      },
    });
  };

  const handleAssignPermissions = (role) => {
    setSelectedRole(role);
    setPermissions(role.permissions || []);
    setIsPermissionsModalVisible(true);
  };

  const handlePermissionsSubmit = () => {
    const updatedRoles = roles.map((r) => (r.id === selectedRole.id ? { ...r, permissions } : r));
    setRoles(updatedRoles);
    setFilteredRoles(updatedRoles);
    setIsPermissionsModalVisible(false);
    message.success(`Permissions updated for "${selectedRole.name}"`);
  };

  const handleViewPermissions = (role) => {
    setSelectedRole(role);
    setIsViewPermissionsModalVisible(true);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field && sorter.order) {
      const sorted = [...filteredRoles].sort((a, b) => {
        const valueA = a[sorter.field].toString().toLowerCase();
        const valueB = b[sorter.field].toString().toLowerCase();
        return sorter.order === 'ascend' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
      setFilteredRoles(sorted);
    }
  };

  const permissionOptions = [
    { label: 'Users: Full Access', value: 'users:*' },
    { label: 'Users: Read Only', value: 'users:read' },
    { label: 'System: Full Access', value: 'system:*' },
    { label: 'System Users: Full Access', value: 'system-users:*' },
    { label: 'Transactions: Full Access', value: 'transactions:*' },
    { label: 'Vendors: Full Access', value: 'vendors:*' },
    { label: 'Reports: Financial', value: 'reports:financial' },
    { label: 'Student: Messages', value: 'student:messages' },
    { label: 'Settings: Full Access', value: 'settings:*' },
  ];

  const columns = [
    { title: 'Role Name', dataIndex: 'name', key: 'name', sorter: true },
    { title: 'Description', dataIndex: 'description', key: 'description', sorter: true },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: true },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => handleViewPermissions(record)}>View Permissions</Button>
          <Button onClick={() => handleAssignPermissions(record)}>Add Permissions</Button>
          <Button
            danger={record.status === 'Active'}
            onClick={() => handleDeactivateRole(record.id)}
          >
            {record.status === 'Active' ? 'Deactivate' : 'Activate'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
      <Title level={2} style={{ marginBottom: 16 }}>Manage Roles</Title>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder="Search roles..."
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Button type="primary" onClick={() => setIsAddModalVisible(true)}>
          Add Role
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRoles}
        loading={loading}
        pagination={{ current: currentPage, pageSize: pageSize, total: filteredRoles.length, showSizeChanger: true }}
        onChange={handleTableChange}
        rowKey="id"
      />

      <Modal
        title="Add New Role"
        open={isAddModalVisible} // Changed from 'visible' for Ant Design v5 compatibility
        onCancel={() => {
          setIsAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddRole}>
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: 'Please enter the role name' }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea placeholder="Enter role description" rows={4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Add Role</Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsAddModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Add Permissions for ${selectedRole?.name || ''}`}
        open={isPermissionsModalVisible} // Changed from 'visible'
        onOk={handlePermissionsSubmit}
        onCancel={() => setIsPermissionsModalVisible(false)}
        okText="Save"
      >
        <Checkbox.Group
          options={permissionOptions}
          value={permissions}
          onChange={(checkedValues) => setPermissions(checkedValues)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        />
      </Modal>

      <Modal
        title={`Permissions for ${selectedRole?.name || ''}`}
        open={isViewPermissionsModalVisible} // Changed from 'visible'
        onOk={() => setIsViewPermissionsModalVisible(false)}
        onCancel={() => setIsViewPermissionsModalVisible(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {selectedRole?.permissions.length > 0 ? (
          <ul>
            {selectedRole.permissions.map((perm, index) => (
              <li key={index}>{perm}</li>
            ))}
          </ul>
        ) : (
          <p>No permissions assigned to this role.</p>
        )}
      </Modal>
    </div>
  );
};

export default ViewRoles;